import { useRef } from 'react';
import { SocialIcon, SocialIconButton } from './SocialIcon';

/// Trình duyệt đọc được các loại này; ảnh luôn được đổi sang JPEG trước khi gửi (prepareImage.ts).
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif';

/**
 * Ô nhập dưới đáy (bình luận, tin nhắn): nút ảnh tuỳ chọn, input pill, nút gửi tròn. Enter = gửi.
 * Có `onPickImage` thì hiện nút chọn ảnh và nhận cả ảnh dán (Ctrl/Cmd+V) vào ô nhập.
 */
export function Composer({
  value,
  onChange,
  onSend,
  sending = false,
  placeholder = 'Nhắn tin…',
  onPickImage,
}: {
  value: string;
  onChange: (text: string) => void;
  onSend: () => void;
  sending?: boolean;
  placeholder?: string;
  onPickImage?: (file: File) => void;
}) {
  const ok = value.trim().length > 0 && !sending;
  const fileInput = useRef<HTMLInputElement | null>(null);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 8px 10px',
        borderTop: '1px solid var(--border-default)',
        background: 'var(--surface-page)',
        flexShrink: 0,
      }}
    >
      {onPickImage && (
        <>
          <SocialIconButton name="image" label="Gửi ảnh" color="var(--cta-default)" onClick={() => fileInput.current?.click()} />
          <input
            ref={fileInput}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Xoá giá trị để chọn lại đúng file đó lần sau vẫn có sự kiện change.
              e.target.value = '';
              if (file) onPickImage(file);
            }}
          />
        </>
      )}
      <input
        className="sk-input"
        aria-label={placeholder.replace('…', '')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // isComposing: đang gõ dở bằng bộ gõ tiếng Nhật/tiếng Việt — Enter là chốt chữ, không phải gửi.
          if (e.key === 'Enter' && !e.nativeEvent.isComposing && ok) onSend();
        }}
        onPaste={(e) => {
          const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
          if (!onPickImage || !file) return;
          e.preventDefault();
          onPickImage(file);
        }}
        placeholder={placeholder}
        style={{
          flex: 1,
          minWidth: 0,
          height: 44,
          borderRadius: 999,
          border: '1px solid var(--border-default)',
          background: 'var(--surface-tint)',
          padding: '0 16px',
          fontFamily: 'var(--font-vi)',
          fontSize: 15,
          color: 'var(--ink)',
          outline: 'none',
          marginLeft: onPickImage ? 0 : 8,
        }}
      />
      <button
        type="button"
        onClick={onSend}
        disabled={!ok}
        aria-label="Gửi"
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: 'none',
          background: ok ? 'var(--cta-default)' : 'var(--surface-tint)',
          color: ok ? '#fff' : 'var(--text-disabled)',
          cursor: ok ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'background .15s ease',
        }}
      >
        <SocialIcon name="send" size={19} />
      </button>
    </div>
  );
}
